const boundedInteger = (value, fallback) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const manifestCount = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : Number.NaN;
};

export const collectAccountExportPages = async ({
  reader,
  organizationId,
  exportId,
  noun = "records",
  expectedCount = null,
  pageSize = 100,
  maxPages = 100,
  maxItems = 10_000,
  unavailableCode = "export_page_unavailable",
  countMismatchCode = "export_item_count_mismatch",
} = {}) => {
  if (typeof reader !== "function") {
    return { ok: false, code: "export_reader_unavailable", error: `The prepared ${noun} reader is unavailable.` };
  }
  if (!organizationId || !exportId) {
    return { ok: false, code: "export_scope_missing", error: `The prepared ${noun} scope is incomplete.` };
  }

  const limit = boundedInteger(pageSize, 100);
  const pageLimit = boundedInteger(maxPages, 100);
  const itemLimit = boundedInteger(maxItems, 10_000);
  const expected = manifestCount(expectedCount);
  if (Number.isNaN(expected)) {
    return { ok: false, code: "export_manifest_invalid", error: `The prepared ${noun} manifest count is invalid.` };
  }
  if (expected !== null && expected > itemLimit) {
    return { ok: false, code: "export_item_limit_exceeded", error: `The prepared ${noun} exceed the browser download limit of ${itemLimit} records.` };
  }

  const items = [];
  const seenCursors = new Set();
  let cursor = null;
  let pageCount = 0;

  do {
    if (pageCount >= pageLimit) {
      return { ok: false, code: "export_page_limit_exceeded", error: `The prepared ${noun} exceeded its bounded page count.` };
    }
    pageCount += 1;

    let page;
    try {
      page = await reader({ organizationId, exportId, limit, cursor });
    } catch (error) {
      return { ok: false, code: unavailableCode, error: error?.message || `The prepared ${noun} could not be loaded.` };
    }
    if (!page?.ok) {
      return { ok: false, code: page?.code || unavailableCode, error: page?.error || `The prepared ${noun} could not be loaded.` };
    }
    if (!Array.isArray(page.data?.items)) {
      return { ok: false, code: "export_page_invalid", error: `The prepared ${noun} returned an invalid page.` };
    }

    items.push(...page.data.items);
    if (items.length > itemLimit) {
      return { ok: false, code: "export_item_limit_exceeded", error: `The prepared ${noun} exceed the browser download limit of ${itemLimit} records.` };
    }

    const nextCursor = page.data?.nextCursor || null;
    if (nextCursor !== null && (typeof nextCursor !== "string" || nextCursor.length > 4096)) {
      return { ok: false, code: "export_cursor_invalid", error: `The prepared ${noun} returned an invalid page cursor.` };
    }
    if (nextCursor && seenCursors.has(nextCursor)) {
      return { ok: false, code: "export_cursor_repeated", error: `The prepared ${noun} returned a repeated page cursor.` };
    }
    if (nextCursor) seenCursors.add(nextCursor);
    cursor = nextCursor;
  } while (cursor);

  if (expected !== null && items.length !== expected) {
    return {
      ok: false,
      code: countMismatchCode,
      error: `The prepared ${noun} count did not match its server manifest.`,
      expectedCount: expected,
      actualCount: items.length,
    };
  }

  return { ok: true, items, pageCount };
};
